import React from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseButton from './Common/CloseButton';
import FlowConditionsSection from './FlowConditionsSection';
import NodeEditorDialogActions from './ProjectDag/NodeEditorDialogActions';
import {
  getTriggerModeOptions,
  getTriggerVisualConfig,
} from '../Utils/workflowTriggerVisuals';
import ConfigurationErrorSummary, {
  CONFIGURATION_ERROR_BACKGROUND,
} from './Common/ConfigurationErrorSummary';
import InlineNodeNameEditor from './Common/InlineNodeNameEditor';
import NodeEditorStatusAlert from './Common/NodeEditorStatusAlert';

const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 360,
    }
  }
};

const TRIGGER_MODE_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.75,
      maxHeight: 'min(480px, calc(100dvh - 80px))',
      maxWidth: 'calc(100vw - 16px)',
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 14px 38px rgba(15, 23, 42, 0.16)',
    }
  },
  MenuListProps: {
    sx: { p: 0.75 }
  }
};

const TriggerModeOptionContent = ({ option, showTooltip = false, tooltipLabel }) => {
  const visualConfig = getTriggerVisualConfig(option.value);
  const TriggerIcon = visualConfig.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1.25 },
        width: '100%',
        minWidth: 0
      }}
    >
      <Box
        sx={{
          width: showTooltip ? { xs: 36, sm: 42 } : { xs: 34, sm: 38 },
          height: showTooltip ? { xs: 36, sm: 42 } : { xs: 34, sm: 38 },
          borderRadius: { xs: 1.5, sm: 2 },
          flex: '0 0 auto',
          display: 'grid',
          placeItems: 'center',
          color: visualConfig.iconColor,
          background: visualConfig.iconBackground,
          border: '1px solid',
          borderColor: visualConfig.borderColor,
        }}
      >
        <TriggerIcon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.3 }}>
          {option.label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.35, whiteSpace: 'normal', mt: 0.2 }}
        >
          {option.description}
        </Typography>
      </Box>

      {showTooltip && (
        <Tooltip
          arrow
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={4000}
          title={(
            <Box sx={{ maxWidth: 300, p: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {option.label}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                {option.tooltip}
              </Typography>
            </Box>
          )}
        >
          <IconButton
            size="small"
            aria-label={`${tooltipLabel}: ${option.label}`}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            sx={{
              flex: '0 0 auto',
              p: { xs: 0.5, sm: 1 },
              color: visualConfig.iconColor,
              bgcolor: visualConfig.labelBackground,
              '&:hover': { bgcolor: visualConfig.iconBackground }
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const EVENT_CHANGE_FILTER_DEFAULT = {
  change_type: 'any',
  operation: 'any',
  tag_variable: '',
  value_type: ''
};

const EVENT_CHANGE_OPERATIONS = {
  any: ['any', 'created', 'updated', 'deleted', 'processed'],
  confirm: ['updated'],
  tag: ['any', 'created', 'updated', 'deleted'],
  date: ['updated'],
  ingestion: ['processed'],
};

const getEventChangeOperations = (changeType) => (
  EVENT_CHANGE_OPERATIONS[changeType] || EVENT_CHANGE_OPERATIONS.any
);

const normalizeEventChangeFilter = (filter) => ({
  ...EVENT_CHANGE_FILTER_DEFAULT,
  ...(filter && typeof filter === 'object' && !Array.isArray(filter) ? filter : {})
});

const renderScheduleSelectValue = (selected, options, placeholder) => {
  const values = Array.isArray(selected) ? selected : [];
  if (!values.length) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {placeholder}
      </Box>
    );
  }

  const optionMap = new Map((options || []).map((option) => [String(option.value), option.label]));
  const labels = values
    .map((value) => optionMap.get(String(value)) ?? String(value))
    .filter(Boolean);

  if (!labels.length) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {placeholder}
      </Box>
    );
  }

  return (
    <Box component="span" sx={{ whiteSpace: 'normal' }}>
      {labels.join(', ')}
    </Box>
  );
};

const ScheduleMultiSelect = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  sx,
}) => {
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedValueSet = new Set(selectedValues.map((item) => String(item)));

  return (
    <FormControl fullWidth size="small" sx={sx}>
      <InputLabel id={`${id}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        multiple
        displayEmpty
        value={value}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selected) => renderScheduleSelectValue(selected, options, placeholder)}
        MenuProps={MULTI_SELECT_MENU_PROPS}
        sx={{
          '& .MuiSelect-select': {
            whiteSpace: 'normal',
          }
        }}
      >
        {(options || []).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValueSet.has(String(option.value))} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const WorkflowFlowAutomationDialog = ({
  open,
  mode,
  flowName,
  showFlowNameOnEdit = false,
  nodeEditorMode = false,
  orderIndex = 0,
  eventConditions = [],
  eventConditionLogic = 'AND',
  projectConditions = [],
  projectConditionLogic = 'AND',
  tags = [],
  dataSources = [],
  triggerMode,
  triggerFilter = EVENT_CHANGE_FILTER_DEFAULT,
  frequencyValue,
  frequencyUnit,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleMonth,
  scheduleHour,
  scheduleMinute,
  scheduleTimezone,
  calendarWeekdayOptions,
  calendarDayOfMonthOptions,
  calendarMonthOptions,
  calendarHourOptions,
  calendarMinuteOptions,
  error,
  statusError = null,
  isSubmitting = false,
  isDeleting = false,
  onClose,
  onSubmit,
  onDelete,
  onFlowNameChange,
  onHeaderFlowNameSave,
  onOrderIndexChange,
  onEventConditionsChange,
  onEventConditionLogicChange,
  onProjectConditionsChange,
  onProjectConditionLogicChange,
  onTriggerModeChange,
  onTriggerFilterChange,
  onFrequencyValueChange,
  onFrequencyUnitChange,
  onScheduleDayOfWeekChange,
  onScheduleDayOfMonthChange,
  onScheduleMonthChange,
  onScheduleHourChange,
  onScheduleMinuteChange,
  t,
}) => {
  const isNodeEditorFlow = nodeEditorMode && mode === 'edit';
  const statusErrorMessage = String(
    typeof statusError === 'string' ? statusError : statusError?.message || ''
  ).trim();
  const nodeEditorErrorMessage = String(error || statusErrorMessage || '').trim();
  const eventChangeFilter = normalizeEventChangeFilter(triggerFilter);
  const updateEventChangeFilter = (updates) => {
    if (!onTriggerFilterChange) {
      return;
    }
    onTriggerFilterChange({
      ...eventChangeFilter,
      ...updates,
    });
  };
  const availableOperations = getEventChangeOperations(eventChangeFilter.change_type);
  const selectedOperation = availableOperations.includes(eventChangeFilter.operation)
    ? eventChangeFilter.operation
    : availableOperations[0];
  const eventChangeTooltipKey = `workflow.automation_event_change_filter_tooltip_${eventChangeFilter.change_type || 'any'}`;
  const eventChangeTooltipFallback = eventChangeFilter.change_type === 'confirm'
    ? 'Confirmation is triggered when an event is confirmed or unconfirmed. It is always an update.'
    : eventChangeFilter.change_type === 'date'
      ? 'Event date is triggered when an existing event date is changed. It is always an update.'
      : eventChangeFilter.change_type === 'tag'
        ? 'Tags can be created, updated, or deleted on an event.'
        : eventChangeFilter.change_type === 'ingestion'
          ? 'Ingestion is triggered when an event has been processed.'
          : 'Choose a change type and, optionally, an operation to control when this flow runs.';
  const triggerModeOptions = getTriggerModeOptions(t);
  const selectedTriggerModeOption = triggerModeOptions.find((option) => option.value === triggerMode)
    || triggerModeOptions[0];
  const triggerModeTooltipLabel = t('workflow.automation_mode_tooltip_label', 'Trigger details');
  const handleTriggerModeSelect = (event) => {
    const nextTriggerMode = event.target.value;
    const nextTriggerModeOption = triggerModeOptions.find(
      (option) => option.value === nextTriggerMode
    );
    const normalizedFlowName = String(flowName || '').trim();
    const genericDefaultName = t('workflow.new_flow_default', 'New Flow');
    const hasAutomaticFlowName = !normalizedFlowName
      || normalizedFlowName === genericDefaultName
      || triggerModeOptions.some((option) => option.label === normalizedFlowName);

    onTriggerModeChange(nextTriggerMode);
    if (
      mode === 'create'
      && hasAutomaticFlowName
      && nextTriggerModeOption
      && onFlowNameChange
    ) {
      onFlowNameChange(nextTriggerModeOption.label);
    }
  };

  return (
  <Dialog
    open={open}
    onClose={isSubmitting || isDeleting ? undefined : onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        m: { xs: 0, sm: 4 },
        width: { xs: '100%', sm: 'calc(100% - 64px)' },
        height: { xs: '100dvh', sm: 'auto' },
        maxHeight: { xs: '100dvh', sm: 'calc(100% - 64px)' },
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: 10
      }
    }}
  >
    <DialogTitle
      data-testid={isNodeEditorFlow ? 'dag-flow-dialog-title' : undefined}
      sx={{
      bgcolor: isNodeEditorFlow
        ? '#fff'
        : statusErrorMessage
          ? undefined
          : 'primary.main',
      background: !isNodeEditorFlow && statusErrorMessage
        ? CONFIGURATION_ERROR_BACKGROUND
        : undefined,
      color: isNodeEditorFlow ? 'inherit' : 'white',
      borderBottom: '1px solid #e0e0e0',
      pr: 1,
      py: { xs: 1.5, sm: 2 },
      fontSize: { xs: '1.1rem', sm: '1.25rem' }
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
      }}>
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          {isNodeEditorFlow ? (
            <InlineNodeNameEditor
              value={flowName}
              onSave={onHeaderFlowNameSave}
              t={t}
            />
          ) : (
            <Typography
              variant="h6"
              component="span"
              sx={{ fontWeight: 600, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            >
              {mode === 'create'
                ? t('workflow.automation_dialog_create_title', 'Create Flow')
                : t('workflow.automation_dialog_edit_title', 'Flow Automation')}
            </Typography>
          )}
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexShrink: 0,
          ml: 'auto',
        }}>
          <CloseButton
            ariaLabel="close"
            onClick={onClose}
            disabled={isSubmitting || isDeleting}
            sx={{ color: isNodeEditorFlow ? 'text.secondary' : 'white' }}
          />
        </Box>
      </Box>
      {isNodeEditorFlow ? (
        nodeEditorErrorMessage && (
          <NodeEditorStatusAlert
            message={nodeEditorErrorMessage}
            testId="dag-flow-status"
            sx={{ mt: 1.5, mb: 0 }}
          />
        )
      ) : (
        <ConfigurationErrorSummary error={statusError} t={t} />
      )}
    </DialogTitle>
    <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: isNodeEditorFlow
          ? { xs: '1fr', sm: '170px' }
          : { xs: '1fr', sm: onOrderIndexChange ? '1fr 170px' : '1fr' },
        justifyContent: isNodeEditorFlow ? 'end' : 'stretch',
        gap: 1.5,
      }}>
        {onOrderIndexChange && !isNodeEditorFlow && <TextField
          fullWidth
          size="small"
          margin="normal"
          label={t('workflow.automation_flow_name_label', 'Flow name')}
          value={flowName}
          onChange={(event) => onFlowNameChange(event.target.value)}
        />}
        {(mode === 'create' || showFlowNameOnEdit) && <TextField
          fullWidth
          size="small"
          margin="normal"
          type="number"
          label={t('workflow.flow_order_label', 'Order')}
          value={orderIndex}
          onChange={(event) => onOrderIndexChange?.(event.target.value)}
          inputProps={{ min: 0, step: 1 }}
          helperText={t('workflow.flow_order_help', 'Lower runs first.')}
        />}
      </Box>

      <FormControl fullWidth sx={{ mt: 1.5, mb: 2 }}>
        <InputLabel id="flow-automation-mode-label">
          {t('workflow.automation_mode_label', 'Trigger mode')}
        </InputLabel>
        <Select
          labelId="flow-automation-mode-label"
          value={triggerMode}
          label={t('workflow.automation_mode_label', 'Trigger mode')}
          onChange={handleTriggerModeSelect}
          renderValue={() => (
            <TriggerModeOptionContent option={selectedTriggerModeOption} />
          )}
          MenuProps={TRIGGER_MODE_MENU_PROPS}
          sx={{
            borderRadius: 2,
            '& .MuiSelect-select': {
              pt: 1.55,
              pb: 1.15,
              pr: '44px !important',
            },
            '& .MuiSelect-select:focus': {
              bgcolor: 'transparent'
            }
          }}
        >
          {triggerModeOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                minHeight: { xs: 58, sm: 66 },
                px: { xs: 0.75, sm: 1.25 },
                py: { xs: 0.75, sm: 1 },
                mb: 0.5,
                borderRadius: 2,
                border: '1px solid transparent',
                whiteSpace: 'normal',
                '&:last-of-type': { mb: 0 },
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  borderColor: getTriggerVisualConfig(option.value).borderColor,
                },
                '&.Mui-selected:hover': { bgcolor: 'action.selected' }
              }}
            >
              <TriggerModeOptionContent
                option={option}
                showTooltip
                tooltipLabel={triggerModeTooltipLabel}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {triggerMode === 'event_change' && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 1,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="flow-event-change-type-label">
              {t('workflow.automation_event_change_type_label', 'Change type')}
            </InputLabel>
            <Select
              labelId="flow-event-change-type-label"
              value={eventChangeFilter.change_type || 'any'}
              label={t('workflow.automation_event_change_type_label', 'Change type')}
              onChange={(event) => {
                const changeType = event.target.value || 'any';
                const updates = { change_type: changeType };
                const operations = getEventChangeOperations(changeType);
                if (!operations.includes(eventChangeFilter.operation)) {
                  updates.operation = operations[0];
                }
                if (changeType !== 'tag') {
                  updates.tag_variable = '';
                  updates.value_type = '';
                }
                updateEventChangeFilter(updates);
              }}
            >
              <MenuItem value="any">{t('workflow.automation_event_change_type_any', 'Any change')}</MenuItem>
              <MenuItem value="confirm">{t('workflow.automation_event_change_type_confirm', 'Confirmation')}</MenuItem>
              <MenuItem value="tag">{t('workflow.automation_event_change_type_tag', 'Tag')}</MenuItem>
              <MenuItem value="date">{t('workflow.automation_event_change_type_date', 'Event date')}</MenuItem>
              <MenuItem value="ingestion">{t('workflow.automation_event_change_type_ingestion', 'Ingestion')}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="flow-event-change-operation-label">
                {t('workflow.automation_event_change_operation_label', 'Operation')}
              </InputLabel>
              <Select
                labelId="flow-event-change-operation-label"
                value={selectedOperation}
                label={t('workflow.automation_event_change_operation_label', 'Operation')}
                onChange={(event) => updateEventChangeFilter({ operation: event.target.value || 'any' })}
              >
                {availableOperations.map((operation) => (
                  <MenuItem key={operation} value={operation}>
                    {t(`workflow.automation_event_change_operation_${operation}`, operation)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={t(eventChangeTooltipKey, eventChangeTooltipFallback)}>
              <IconButton
                size="small"
                aria-label={t('workflow.automation_event_change_filter_tooltip_label', 'Explain this filter')}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {eventChangeFilter.change_type === 'tag' && (
            <>
              <TextField
                fullWidth
                size="small"
                label={t('workflow.automation_event_change_tag_variable_label', 'Tag variable')}
                value={eventChangeFilter.tag_variable || ''}
                onChange={(event) => updateEventChangeFilter({ tag_variable: event.target.value })}
                placeholder={t('workflow.automation_event_change_tag_variable_placeholder', 'status')}
              />
              <TextField
                fullWidth
                size="small"
                label={t('workflow.automation_event_change_value_type_label', 'Value type')}
                value={eventChangeFilter.value_type || ''}
                onChange={(event) => updateEventChangeFilter({ value_type: event.target.value })}
                placeholder={t('workflow.automation_event_change_value_type_placeholder', 'text')}
              />
            </>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block', mt: -0.25 }}
          >
            {t('workflow.automation_event_change_filter_help', 'Leave fields on Any or empty to match all event detail changes.')}
          </Typography>
        </Box>
      )}

      {triggerMode === 'time' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', sm: 'center' },
            mb: 1
          }}
        >
          <TextField
            type="number"
            size="small"
            label={t('workflow.automation_frequency_value_label', 'Every')}
            value={frequencyValue}
            onChange={(event) => onFrequencyValueChange(event.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel id="flow-automation-unit-label">
              {t('workflow.automation_frequency_unit_label', 'Unit')}
            </InputLabel>
            <Select
              labelId="flow-automation-unit-label"
              value={frequencyUnit}
              label={t('workflow.automation_frequency_unit_label', 'Unit')}
              onChange={(event) => onFrequencyUnitChange(event.target.value)}
            >
              <MenuItem value="minutes">{t('workflow.automation_unit_minutes', 'minutes')}</MenuItem>
              <MenuItem value="hours">{t('workflow.automation_unit_hours', 'hours')}</MenuItem>
              <MenuItem value="days">{t('workflow.automation_unit_days', 'days')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {triggerMode === 'calendar' && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 1,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block', mt: -0.25 }}
          >
            {t('workflow.automation_calendar_help', 'Leave a field empty to match any value.')}
          </Typography>

          <ScheduleMultiSelect
            id="flow-automation-weekdays"
            label={t('workflow.automation_weekdays_label', 'Weekdays')}
            value={scheduleDayOfWeek}
            onChange={onScheduleDayOfWeekChange}
            options={calendarWeekdayOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
            sx={{ gridColumn: '1 / -1' }}
          />

          <ScheduleMultiSelect
            id="flow-automation-days-of-month"
            label={t('workflow.automation_days_of_month_label', 'Days of month')}
            value={scheduleDayOfMonth}
            onChange={onScheduleDayOfMonthChange}
            options={calendarDayOfMonthOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-months"
            label={t('workflow.automation_months_label', 'Months')}
            value={scheduleMonth}
            onChange={onScheduleMonthChange}
            options={calendarMonthOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-hours"
            label={t('workflow.automation_hours_label', 'Hours')}
            value={scheduleHour}
            onChange={onScheduleHourChange}
            options={calendarHourOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-minutes"
            label={t('workflow.automation_minutes_label', 'Minutes')}
            value={scheduleMinute}
            onChange={onScheduleMinuteChange}
            options={calendarMinuteOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block' }}
          >
            {t('workflow.automation_calendar_timezone_help', 'Times are saved in {{timezone}}. Empty fields match any value.', {
              timezone: scheduleTimezone || 'UTC'
            })}
          </Typography>
        </Box>
      )}

      {onEventConditionsChange && onProjectConditionsChange && <FlowConditionsSection
        eventConditions={eventConditions}
        eventConditionLogic={eventConditionLogic}
        projectConditions={projectConditions}
        projectConditionLogic={projectConditionLogic}
        tags={tags}
        dataSources={dataSources}
        onEventConditionsChange={onEventConditionsChange}
        onEventConditionLogicChange={onEventConditionLogicChange}
        onProjectConditionsChange={onProjectConditionsChange}
        onProjectConditionLogicChange={onProjectConditionLogicChange}
        t={t}
      />}

      {error && !isNodeEditorFlow && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </DialogContent>
    <NodeEditorDialogActions
      onDelete={onDelete}
      onCancel={onClose}
      onSave={onSubmit}
      deleteDisabled={isSubmitting || isDeleting}
      cancelDisabled={isSubmitting || isDeleting}
      saveDisabled={isSubmitting || isDeleting}
      saving={isSubmitting}
      saveLabel={mode === 'create'
        ? t('workflow.automation_create_submit', 'Create Flow')
        : t('common.save', 'Save')}
    />
  </Dialog>
  );
};

export default WorkflowFlowAutomationDialog;
